import axios, { AxiosRequestConfig } from 'axios';
import Token from 'types/models/Token';
import BaseApiService from './BaseApiService';
import InMemoryAuthApiService from './InMemory/InMemoryAuthApiService';
import getCompanyByHeader from 'helpers/getCompanyByHeader';
import { getSubdomain } from 'helpers/utils';

export interface IAuthApiService {
  getAuthAuthorization(): Promise<string>;
  getAuthToken(tokenUri: string): Promise<Token>;
  getTokenUri(code: string): Promise<{ tokenUri: string }>;
  getAuthCode(authUri: string): Promise<{ tokenUri: string }>;
  getRefreshTokenUri(refresh_token: string): Promise<{ tokenUri: string }>;
}
class AuthApiService extends BaseApiService {
  private baseUrl: string;

  public companyName: string | null = null;

  private secret: string | null = null;

  private redirectUri: string | null = null;

  private axiosAuthConfig: AxiosRequestConfig;

  constructor() {
    super();
    const currentHostname = window.location.hostname;
    const currentSub = getSubdomain(currentHostname);
    const company = getCompanyByHeader(currentSub);
    this.baseUrl = `${process.env.REACT_APP_SERVER_URL}/auth`;
    const { clientId, clientSecret, domainPrefix, redirectUri } = company.cognito;
    this.redirectUri = redirectUri;
    this.secret = Buffer.from(`${clientId}:${clientSecret}`).toString('base64');
    this.companyName = domainPrefix;
    this.axiosAuthConfig = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Access-Control-Allow-Origin': '*',
        'X-Sub-Domain': domainPrefix,
      },
    };
    this.verifyParams();
  }

  async getAuthAuthorization(): Promise<string> {
    const res = await axios.get(`${this.baseUrl}/authorize?redirect_uri=${this.redirectUri}`, {
      ...this.axiosAuthConfig,
      headers: {
        'X-Sub-Domain': this.companyName,
      },
    });
    return res.data.authUri;
  }

  async getAuthAuthorizationLogout(): Promise<string> {
    const tokenStr = localStorage.getItem('cognito-token');
    let authHeader = {};
    if (tokenStr) {
      try {
        const tokenObj = JSON.parse(tokenStr);
        const idToken = tokenObj.id_token;

        authHeader = {
          Authorization: `Bearer ${idToken}`,
        };
      } catch (e) {
        console.error('Failed to parse cognito-token:', e);
      }
    }
    console.log('tokenStr: ', tokenStr);
    // const res = await axios.get(`${this.baseUrl}/authorize?redirect_uri=${this.redirectUri}`, this.axiosAuthConfig);
    const res = await axios.get(`${this.baseUrl}/authorize?redirect_uri=${this.redirectUri}`, {
      ...this.axiosAuthConfig,
      headers: {
        ...this.axiosAuthConfig.headers,
        ...authHeader,
      },
    });
    return res.data.authUri;
  }

  async getAuthToken(tokenUri: string) {
    try {
      const res = await axios.post(
        tokenUri,
        {},
        {
          ...this.axiosAuthConfig,
          headers: {
            ...this.axiosAuthConfig.headers,
            'content-type': 'application/x-www-form-urlencoded',
            Authorization: `Basic ${this.secret}`,
          },
        }
      );

      this.setLocalStorageToken(res.data);
      return res.data;
    } catch (error) {
      return error;
    }
  }

  async getTokenUri(code: string): Promise<{ tokenUri: string }> {
    const res = await axios.post(
      `${this.baseUrl}/token?code=${code}&redirect_uri=${this.redirectUri}`,
      this.axiosAuthConfig,
      {
        headers: {
          'X-Sub-Domain': this.companyName,
        },
      }
    );
    return res.data;
  }

  async getAuthCode(authUri: string): Promise<{ tokenUri: string }> {
    return axios.get(authUri, this.axiosAuthConfig);
  }

  async getRefreshTokenUri(refresh_token: string): Promise<{ tokenUri: string }> {
    const res = await axios.post(
      `${this.baseUrl}/refresh`,
      { refresh_token },
      {
        headers: {
          'X-Sub-Domain': this.companyName,
        },
      }
    );
    return res.data;
  }

  // eslint-disable-next-line class-methods-use-this
  public setLocalStorageToken(data: Token): void {
    const tokenAlreadyPresent = localStorage.getItem('cognito-token');
    let dataToStock = data;
    if (tokenAlreadyPresent) {
      dataToStock = { ...JSON.parse(tokenAlreadyPresent), ...data };
    }
    // eslint-disable-next-line no-param-reassign
    data.expires_at = (Date.now() + (data.expires_in as number)) as number;
    localStorage.setItem('cognito-token', JSON.stringify(dataToStock));
  }

  private verifyParams() {
    if (!this.secret) {
      throw new Error('Missing secret');
    }
  }
}
const isTestMode = process.env.REACT_APP_NODE_ENV === 'test';
const authApiService = isTestMode ? new InMemoryAuthApiService() : new AuthApiService();

export default authApiService;
